import type { MouseEvent } from 'react';
import { isNativeApp } from '../lib/nativeApp';

/**
 * Making the app's clickables behave like links.
 *
 * Every in-app target has a real URL (the record dialogs are routes), but a
 * `<ListItemButton onClick={navigate}>` renders a `<div>` — with no `href`
 * there is nothing for the browser to open in a new tab, so middle click,
 * Ctrl/Cmd click and "open link in new tab" all do nothing. Rendering the same
 * component `component={RouterLink} to={…}` emits a real `<a href>`.
 *
 * What still has to run in-app on a PLAIN click is our own click handling —
 * closing the drawer, and the unsaved-changes guard. Hence {@link linkClick}.
 */

/**
 * True for a click the BROWSER should be left to handle: one with a modifier
 * held, or a non-primary button.
 *
 * A middle click never reaches a React `onClick` at all (browsers fire
 * `auxclick`), so in practice this is about Ctrl/Cmd/Shift/Alt.
 *
 * Never in the Android app: it has no tabs, and a link left to the WebView
 * would reload the whole app on that page instead of navigating within it.
 */
export function opensOutsideTheApp(event: MouseEvent): boolean {
    if (isNativeApp()) {
        return false;
    }
    return (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
    );
}

/**
 * A link chip whose text can be selected and copied like the text beside it.
 * MUI's ButtonBase switches selection off, and a dragged `<a>` is picked up as
 * a link instead of selecting — so pair this with `draggable={false}`, and let
 * {@link endsTextSelection} keep the drag's closing click from navigating.
 */
export const SELECTABLE_LINK_SX = { userSelect: 'text' } as const;

/**
 * True when the click only ends a drag that selected text inside the link: the
 * mouse went down and up on it, but the reader was copying, not opening.
 */
export function endsTextSelection(event: MouseEvent<Element>): boolean {
    const selection = window.getSelection();
    return Boolean(
        selection &&
        !selection.isCollapsed &&
        selection.containsNode(event.currentTarget, true)
    );
}

/**
 * The second click of a double-click. The first one already opened the record;
 * acting on this one as well pushed the same route twice, and closing the
 * dialog then led back to it instead of out.
 */
export function repeatsClick(event: MouseEvent): boolean {
    return event.detail > 1;
}

/**
 * `onDoubleClick` for a link chip inside a grid cell: the double-click stops
 * at the chip. Bubbling on, it reached the grid's `onCellDoubleClick`, which
 * opens the row's record — a second time, on top of the chip's own.
 */
export function stopDoubleClick(event: MouseEvent) {
    event.stopPropagation();
}

/**
 * `onClick` for a clickable rendered as a router link: a plain click runs `go`
 * in-app (so drawers still close and unsaved-changes guards still prompt),
 * while a new-tab click falls through to the browser untouched.
 *
 * Pair it with `component={RouterLink} to={…}` — without the href there is
 * nothing to fall through TO.
 */
export function linkClick(go: () => void) {
    return (event: MouseEvent<HTMLElement>) => {
        if (opensOutsideTheApp(event)) {
            return;
        }
        event.preventDefault();
        go();
    };
}
